import {
  Column,
  Row as ReactGridRow,
  DefaultCellTypes,
} from '@silevis/reactgrid';

export const buildQueryString = (params: Record<string, any>): string => {
  const query = new URLSearchParams();
  if (params.dateRange && params.dateRange.length === 2) {
    const s = new Date(params.dateRange[0]);
    const e = new Date(params.dateRange[1]);
    query.append(
      'startDate',
      new Date(
        Date.UTC(s.getFullYear(), s.getMonth(), s.getDate(), 0, 0, 0, 0)
      ).toISOString()
    );
    query.append(
      'endDate',
      new Date(
        Date.UTC(e.getFullYear(), e.getMonth(), e.getDate(), 23, 59, 59, 999)
      ).toISOString()
    );
  }
  if (params.differentiator)
    query.append('differentiator', params.differentiator);
  if (params.compareKey)
    params.compareKey.forEach((c: string) => query.append('compareKey', c));
  if (params.fieldFilters) {
    params.fieldFilters.forEach((filter: any) => {
      if (Array.isArray(filter.fieldValue)) {
        filter.fieldValue.forEach((v: string) =>
          query.append(filter.fieldKey, v)
        );
      }
    });
  }
  return query.toString();
};

export const buildTableData = (
  dataList: any,
  hasSecondAxis: boolean,
  labelColWidth: number,
  colWidth: number,
  rightAxisWidth: number
) => {
  const allDifferentiators = new Set<string>();
  const allMetrics = new Set<string>();
  const allTimelines = dataList.differentiatorData.map((d: any) => d.timeline);

  dataList.differentiatorData.forEach((entry: any) => {
    Object.entries(entry.differentiatorValues).forEach(([branch, metrics]) => {
      allDifferentiators.add(branch);
      Object.keys(metrics as object).forEach((m) => allMetrics.add(m));
    });
  });

  const sortedMetrics = Array.from(allMetrics);
  const sortedTimelines = allTimelines;
  const sortedDifferentiators = Array.from(allDifferentiators);

  const tableColumns: Column[] =
    dataList.differentiatorData.length > 0
      ? [
          { columnId: 'label', width: labelColWidth },
          ...sortedTimelines.flatMap((timeline) =>
            sortedMetrics.map((metric) => ({
              columnId: `${timeline}-${metric}`,
              width: colWidth,
            }))
          ),
          ...(hasSecondAxis
            ? [{ columnId: 'rightPad', width: rightAxisWidth }]
            : []),
        ]
      : [];

  const metricsHeader: ReactGridRow<DefaultCellTypes> = {
    rowId: 'metrics-header',
    height: 40,
    cells: [
      { type: 'text' as const, text: 'Slice Data On' },
      ...sortedTimelines.flatMap(() =>
        sortedMetrics.map((metric) => ({ type: 'text' as const, text: metric }))
      ),
      ...(hasSecondAxis ? [{ type: 'text' as const, text: '' }] : []),
    ],
  };

  const tableRows: ReactGridRow<any>[] = sortedDifferentiators.map((diff) => ({
    rowId: diff,
    height: 40,
    cells: [
      { type: 'text', text: diff },
      ...sortedTimelines.flatMap((timeline) => {
        const block = dataList.differentiatorData.find(
          (d: any) => d.timeline === timeline
        );
        const metrics = block?.differentiatorValues?.[diff];
        return sortedMetrics.map((m) => ({
          type: 'text',
          text: metrics?.[m]?.toLocaleString() ?? '0',
        }));
      }),
      ...(hasSecondAxis ? [{ type: 'text', text: '' }] : []),
    ],
  }));

  return { tableColumns, metricsHeader, tableRows };
};
